import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "../../api";
import { Card, Button, TextField, Grid, Box } from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import Typography from "@mui/material/Typography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "components/AlertMessages/SnackbarContext";
import SoftTypography from "components/SoftTypography";
import SoftBox from "components/SoftBox";
import AWS from "aws-sdk";
import { useDropzone } from "react-dropzone";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import PageLoader from "examples/ReuseFunctions/PageLoader";
import { AxiosError } from "axios";

const validationSchema = Yup.object({
  page_name: Yup.string().required("Name is required"),
  page_title: Yup.string().required("Title is required"),
  page_description: Yup.string().required("Description is required"),
});

const s3 = new AWS.S3({
  endpoint: process.env.REACT_APP_AWS_ENDPOINT,
  accessKeyId: process.env.REACT_APP_AWS_ACCESS_KEY_ID,
  secretAccessKey: process.env.REACT_APP_AWS_SECRET_ACCESS_KEY,
  region: process.env.REACT_APP_AWS_REGION,
  signatureVersion: "v4",
});

interface UploadedFile {
  name: string;
  type: string;
  size: number;
  preview: string;
}

interface Page {
  page_name: string;
  page_title: string;
  page_description: string;
  page_image: string;
}

export interface EditPageProps {
  id?: string;
  embedded?: boolean;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const EditPage = ({ id: idProp, embedded, onSuccess, onCancel }: EditPageProps = {}) => {
  const { id: idFromParams } = useParams();
  const id = idProp ?? idFromParams;
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const { fetchError, fetchSuccess } = useSnackbar();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [pages, setPages] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchPages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/pages/${id}`);
      const { data } = response;
      setPages(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      throw error;
    }
  }, [id]);

  useEffect(() => {
    fetchPages();
  }, [fetchPages]);

  const dropzone = useDropzone({
    accept: {
      "image/png": [".png", ".jpeg", ".jpg"],
      "video/mp4": [".mp4"],
    },
    onDrop: (acceptedFiles) => {
      // Append new files to the existing uploadedFiles array
      setUploadedFiles((prevFiles) => [
        ...prevFiles,
        ...acceptedFiles.map((file) =>
          Object.assign(file, {
            preview: URL.createObjectURL(file),
          })
        ),
      ]);
    },
  });

  useEffect(() => {
    const filesWithPreview = dropzone.acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    ) as UploadedFile[];

    setUploadedFiles(filesWithPreview);
  }, [dropzone.acceptedFiles]);

  if (loading) {
    return <PageLoader />;
  }

  const formContent = (
    <Box sx={{ width: "100%", ...(!embedded && { p: 3, borderRadius: 2, boxShadow: 2 }) }}>
      <Typography variant="h5" sx={{ marginBottom: 2 }}>
        Edit Page
      </Typography>
      <Formik
          initialValues={{
            page_name: pages?.page_name || "",
            page_title: pages?.page_title || "",
            page_description: pages?.page_description || "",
            page_image: pages?.page_image || "",
          }}
          validationSchema={validationSchema}
          onSubmit={async (values) => {
            try {
              if (uploadedFiles && uploadedFiles.length > 0) {
                const file = uploadedFiles[0];
                const fileName = `${Date.now()}-${file.name}`;
                values.page_image = fileName;
                const bucketName = process.env.REACT_APP_AWS_BUCKET_NAME;

                if (!bucketName) {
                  fetchError("AWS bucket configuration is missing");
                  return;
                }
                const params: AWS.S3.PutObjectRequest = {
                  Bucket: bucketName,
                  Key: fileName,
                  Body: file,
                  ContentType: file.type,
                  ACL: "public-read",
                };

                s3.upload(params, async (err, data) => {
                  if (err) {
                    console.error("Error uploading file:", err);
                  } else {
                    try {
                      const response = await axios.put(`/pages/${id}`, values);
                      fetchSuccess(response.data.message);
                      if (embedded && onSuccess) onSuccess();
                      else navigate("/pages-list");
                    } catch (error) {
                      if (error instanceof AxiosError && error.response) {
                        fetchError(error.response.data.error);
                      }
                    }
                  }
                });
              } else {
                const response = await axios.put(`/pages/${id}`, values);
                fetchSuccess(response.data.message);
                if (embedded && onSuccess) onSuccess();
                else navigate("/pages-list");
              }
            } catch (error) {
              if (error instanceof AxiosError && error.response) {
                fetchError(error.response.data.error);
              }
            }
          }}
        >
          {({
            values,
            errors,
            touched,
            setFieldValue,
            handleChange,
            handleBlur,
          }) => (
            <Form
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "20px",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Page Name
                    </SoftTypography>
                    <TextField
                      name="page_name"
                      fullWidth
                      variant="outlined"
                      value={values.page_name}
                      onChange={handleChange}
                      error={!!errors.page_name && touched.page_name}
                      helperText={touched.page_name && errors.page_name}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Page Title
                    </SoftTypography>
                    <TextField
                      name="page_title"
                      fullWidth
                      variant="outlined"
                      value={values.page_title}
                      onChange={handleChange}
                      error={!!errors.page_title && touched.page_title}
                      helperText={touched.page_title && errors.page_title}
                    />
                  </SoftBox>
                </Grid>
                <Grid item xs={12}>
                  <SoftBox sx={{ display: "block" }}>
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ display: "block", mb: 0.5 }}>
                      Page Description
                    </SoftTypography>
                    <ReactQuill
                      ref={editorRef}
                      value={values.page_description}
                      onChange={(content, delta, source, editor) => {
                        setFieldValue("page_description", content);
                      }}
                      onBlur={(range, source, quill) => {
                        handleBlur({ target: { name: "page_description" } });
                      }}
                      modules={{
                        toolbar: [
                          [{ header: "1" }, { header: "2" }, { font: [] }],
                          [{ list: "ordered" }, { list: "bullet" }],
                          [
                            "bold",
                            "italic",
                            "underline",
                            "strike",
                            "blockquote",
                          ],
                          [{ align: [] }],
                          ["link", "image"],
                          ["clean"],
                        ],
                      }}
                      formats={[
                        "header",
                        "font",
                        "list",
                        "bold",
                        "italic",
                        "underline",
                        "strike",
                        "blockquote",
                        "align",
                        "link",
                        "image",
                      ]}
                    />
                    {touched.page_description && errors.page_description && (
                      <div
                        className="error"
                        style={{
                          color: "#ea0606",
                          fontSize: "13px",
                          marginLeft: "13px",
                          marginTop: "6px",
                        }}
                      >
                        {errors.page_description}
                      </div>
                    )}
                  </SoftBox>
                </Grid>
              </Grid>
              <Grid item xs={12} md={6}>
                <div>
                  {pages?.page_image && uploadedFiles.length === 0 ? (
                    <div
                      className="media-container"
                      style={{ position: "relative" }}
                    >
                      <img
                        className="upload-img"
                        src={`${process.env.REACT_APP_CLOUD_FRONT_URL}/${pages?.page_image}`}
                        alt="page-media"
                      />
                    </div>
                  ) : (
                    uploadedFiles[0]?.preview && (
                      <img
                        src={uploadedFiles[0]?.preview}
                        alt="media"
                        className="upload-img"
                      />
                    )
                  )}
                </div>
                <Box
                  sx={{ cursor: "pointer", width: "fit-content" }}
                  {...dropzone.getRootProps()}
                >
                  <Box>
                    <label htmlFor="image-upload-input">
                      <input
                        {...dropzone.getInputProps({
                          name: "page_image",
                        })}
                      />
                      <Button
                        className="upload-btn"
                        component="span"
                        variant="contained"
                        color="primary"
                      >
                        Add/Upload File
                      </Button>
                    </label>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Button
                    className="success-btn"
                    sx={{
                      position: "relative",
                      minWidth: 100,
                    }}
                    variant="contained"
                    type="submit"
                    color="success"
                  >
                    Save
                  </Button>
                  <Button
                    className="cancel-btn"
                    sx={{
                      position: "relative",
                      minWidth: 100,
                      ml: 2.5,
                    }}
                    variant="contained"
                    color="warning"
                    onClick={() =>
                      embedded && onCancel ? onCancel() : navigate("/pages-list")
                    }
                    type="button"
                  >
                    Cancel
                  </Button>
                </Box>
              </Grid>
            </Form>
          )}
        </Formik>
    </Box>
  );

  if (embedded) return formContent;
  return (
    <DashboardLayout>
      <DashboardNavbar />
      {formContent}
    </DashboardLayout>
  );
};

export default EditPage;
